import { formatDateTime } from './RiskCard';
import RiskHitFeatureBlock from './RiskHitFeatureBlock';

const DEFAULT_SEND_HOUR_MSK = 18;

function formatDayTick(ms) {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
  }).format(new Date(ms));
}

function mskParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date instanceof Date ? date : new Date(date));
  const get = (type) => Number(parts.find((part) => part.type === type)?.value);
  return {
    year: get('year'),
    month: get('month') - 1,
    day: get('day'),
    hour: get('hour'),
  };
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function addMonths(year, month, delta) {
  const total = year * 12 + month + delta;
  return {
    year: Math.floor(total / 12),
    month: ((total % 12) + 12) % 12,
  };
}

function mskInstant(year, month, day, hour = DEFAULT_SEND_HOUR_MSK) {
  const clamped = Math.min(Math.max(day, 1), daysInMonth(year, month));
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(clamped).padStart(2, '0');
  const hh = String(hour).padStart(2, '0');
  return new Date(`${year}-${mm}-${dd}T${hh}:00:00+03:00`).getTime();
}

/**
 * Next schedule Instant strictly after `nowMs` (MSK day-of-month cadence).
 */
function nextFutureTransmission(nowMs, dayOfMonth, hour = DEFAULT_SEND_HOUR_MSK) {
  if (!Number.isFinite(dayOfMonth) || dayOfMonth <= 0) {
    return null;
  }
  const parts = mskParts(nowMs);
  let year = parts.year;
  let month = parts.month;
  for (let guard = 0; guard < 36; guard += 1) {
    const t = mskInstant(year, month, dayOfMonth, hour);
    if (t > nowMs) {
      return t;
    }
    const next = addMonths(year, month, 1);
    year = next.year;
    month = next.month;
  }
  return null;
}

/**
 * All day-of-month transmission Instants on the visible axis (MSK).
 */
function buildPeriodTransmissionTimes(min, max, dayOfMonth, hour = DEFAULT_SEND_HOUR_MSK) {
  if (!Number.isFinite(dayOfMonth) || dayOfMonth <= 0) {
    return [];
  }
  const start = mskParts(min);
  const end = mskParts(max);
  let year = start.year;
  let month = start.month;
  const endIndex = end.year * 12 + end.month;
  const times = [];
  let guard = 0;
  while (year * 12 + month <= endIndex && guard < 240) {
    guard += 1;
    const t = mskInstant(year, month, dayOfMonth, hour);
    if (t >= min && t <= max) {
      times.push(t);
    }
    const next = addMonths(year, month, 1);
    year = next.year;
    month = next.month;
  }
  return times;
}

/**
 * Always: failure → now → next future transmission (green).
 * Missed Instants on the axis are yellow unmarked dots; scale dates follow transmissions.
 */
export default function TransmissionScheduleTimeline({
  failureAt,
  transmissionAt,
  now = new Date(),
  scheduleLabel = null,
  sendAtDay = null,
  sendAtHour = DEFAULT_SEND_HOUR_MSK,
}) {
  const failure = failureAt ? new Date(failureAt) : null;
  const transmissionHint = transmissionAt ? new Date(transmissionAt) : null;
  const current = now instanceof Date ? now : new Date(now);

  if (!failure || Number.isNaN(failure.getTime())) {
    return null;
  }

  const dayOfMonth =
    Number.isFinite(Number(sendAtDay)) && Number(sendAtDay) > 0
      ? Number(sendAtDay)
      : transmissionHint && !Number.isNaN(transmissionHint.getTime())
        ? mskParts(transmissionHint).day
        : null;
  const hour =
    Number.isFinite(Number(sendAtHour)) && Number(sendAtHour) >= 0
      ? Number(sendAtHour)
      : DEFAULT_SEND_HOUR_MSK;

  if (!dayOfMonth) {
    return null;
  }

  const nowMs = current.getTime();
  const labeledTransmissionT = nextFutureTransmission(nowMs, dayOfMonth, hour);
  if (labeledTransmissionT == null) {
    return null;
  }

  const min = Math.min(failure.getTime(), nowMs, labeledTransmissionT);
  const max = Math.max(failure.getTime(), nowMs, labeledTransmissionT);
  const span = Math.max(max - min, 1);
  const pct = (t) => `${Math.min(100, Math.max(0, ((t - min) / span) * 100))}%`;

  const periodDotClass = (t) =>
    t <= nowMs
      ? 'border-amber-500/80 bg-amber-400/80'
      : 'border-emerald-500 bg-white dark:bg-slate-950';

  const labeledParts = mskParts(labeledTransmissionT);
  const transmissionTimes = buildPeriodTransmissionTimes(min, max, dayOfMonth, hour);
  const dayTicks = transmissionTimes;
  const periodTimes = transmissionTimes.filter((t) => {
    const parts = mskParts(t);
    return !(
      parts.year === labeledParts.year &&
      parts.month === labeledParts.month &&
      parts.day === labeledParts.day
    );
  });

  const markers = [
    {
      key: 'failure',
      t: failure.getTime(),
      title: 'Сбой',
      when: formatDateTime(failure),
      dotClass: 'border-rose-500 bg-white dark:bg-slate-950',
    },
    {
      key: 'transmission',
      t: labeledTransmissionT,
      title: 'Передача',
      when: formatDateTime(labeledTransmissionT),
      // Always the next Instant ahead — green.
      dotClass: 'border-emerald-500 bg-white dark:bg-slate-950',
    },
    {
      key: 'now',
      t: nowMs,
      title: 'Сейчас',
      when: formatDateTime(current),
      dotClass: 'border-sky-500 bg-white dark:bg-slate-950',
    },
  ].sort((a, b) => a.t - b.t || a.key.localeCompare(b.key));

  return (
    <RiskHitFeatureBlock
      title={
        <>
          День передачи из настройки
          {scheduleLabel ? ` · ${scheduleLabel}` : ''}
        </>
      }
    >
      {/*
        Scale lives in an inner box; outer padding gives room for end-dot radius.
        pct 0%/100% = true ends of the track (not remapped). No horizontal scroll.
      */}
      <div className="h-[5.5rem] px-4">
        <div className="relative h-full w-full">
          <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-slate-200 dark:bg-slate-800" />

          {periodTimes.map((t) => (
            <div
              key={`period-${t}`}
              className={`absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${periodDotClass(t)}`}
              style={{ left: pct(t) }}
              title={`Передача: ${formatDateTime(t)}`}
            />
          ))}

          {markers.map((marker, index) => {
            const below = index % 2 === 0;
            const isLeftmost = index === 0;
            const isRightmost = index === markers.length - 1;
            const alignClass = isLeftmost
              ? 'text-left'
              : isRightmost
                ? '-translate-x-full text-right'
                : '-translate-x-1/2 text-center';
            const labelLeft = isLeftmost
              ? pct(marker.t)
              : isRightmost
                ? pct(marker.t)
                : pct(marker.t);
            return (
              <div key={marker.key}>
                <div
                  className={`absolute top-1/2 z-[1] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${marker.dotClass}`}
                  style={{ left: pct(marker.t) }}
                  title={`${marker.title}: ${marker.when}`}
                />
                <div
                  className={`absolute z-[1] w-[5.25rem] text-[10px] leading-tight text-slate-500 ${alignClass} ${
                    below ? 'top-[calc(50%+0.55rem)]' : 'bottom-[calc(50%+0.55rem)]'
                  }`}
                  style={{ left: labelLeft }}
                >
                  <p className="font-medium text-slate-600 dark:text-slate-300">{marker.title}</p>
                  <p className="tabular-nums">{marker.when}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-0.5 h-3.5 px-4">
        <div className="relative h-full w-full">
          {dayTicks.map((t, index) => {
            const isFirst = index === 0;
            const isLast = index === dayTicks.length - 1;
            const alignClass = isFirst
              ? 'translate-x-0 text-left'
              : isLast
                ? '-translate-x-full text-right'
                : '-translate-x-1/2 text-center';
            return (
              <span
                key={t}
                className={`absolute whitespace-nowrap text-[10px] tabular-nums text-slate-400 ${alignClass}`}
                style={{ left: pct(t) }}
              >
                {formatDayTick(t)}
              </span>
            );
          })}
        </div>
      </div>
    </RiskHitFeatureBlock>
  );
}
